import CircleUser from '@/models/circleUser'
import DisplayUser from '@/models/displayUser'
import User from '@/models/user'
import PostLocation from '@/models/postLocation'
import Category from '@/models/category'
import City from '@/models/city'
import Location from '@/models/location'
import ImageAssetOrUri from '@/models/imageAssetOrUri'
import { addMinutes, eachDayOfInterval, format } from 'date-fns'
import Like from '@/models/like'
import Save from '@/models/save'
import { warn, error, debug } from '@/utils/logger'
import { getPhotoUrl, type PhotoIdentifier } from '@/utils/photoUtils'
import type { ListPost } from './listTypes'
import type { ItineraryPost } from './itineraryTypes'
import type { PrimaryDestination } from '@/types/city'
import { coerceStringIdArray } from '@/utils/coerceStringIdArray'
import { isPostLocationRowArray } from '@/utils/postLocationStopIdentity'

export enum PostType {
  Question = 'Question',
  Comment = 'Comment',
  Place = 'Place',
  List = 'List',
  Itinerary = 'Itinerary',
  Booking = 'Booking',
  Community = 'Community',
  Reply = 'Reply'
}

export class ItineraryDestination {
  name: string
  label: string
  location_id: string
  geoposition?: {
    type: string
    coordinates: number[]
  }
  id: string
  google_place_id: string
  a3_intl_address?: string

  constructor(data: Location) {
    this.id = data._id || ''
    this.name = data.name || data.city_identifying_name || ''
    this.label = data.name || data?.label || ''
    this.location_id = data._id
    this.geoposition = data.geoposition
    this.google_place_id = data.google_place_id || ''
    this.a3_intl_address = data.a3_intl_address || ''
  }
}

export class ItineraryData {
  is_using_day_count: boolean
  user_defined_destinations: ItineraryDestination[]
  day_count: number
  traveler_count: number
  start_date_string?: string
  end_date_string?: string
  /** Present on some API payloads; not used by all flows */
  day_notes?: unknown

  get datesAsString(): string | undefined {
    return this.formatDateAsString()
  }

  get dateAsShortString(): string | undefined {
    return this.formatDateAsString(true)
  }

  get dataString(): string | undefined {
    if (this.is_using_day_count) {
      return `${this.day_count} ${this.day_count === 1 ? 'Day' : 'Days'}`
    } else {
      return this.datesAsString
    }
  }

  get dataStringShort(): string | undefined {
    if (this.is_using_day_count) {
      return `${this.day_count} ${this.day_count === 1 ? 'Day' : 'Days'}`
    } else {
      return this.dateAsShortString
    }
  }

  get selectableDaysIndexArray(): number[] {
    if (this.is_using_day_count) {
      return [...Array(this.day_count).keys()]
    } else {
      return [...Array(this.selectableDatesArray.length).keys()]
    }
  }

  get selectableDaysArray(): string[] {
    if (this.is_using_day_count) {
      return this.selectableDaysIndexArray.map(i => `Day ${i + 1}`)
    } else {
      return this.selectableDatesArray
    }
  }

  get selectableDatesArray(): string[] {
    const timeZoneOffsetMinutes = new Date('2024-01-01').getTimezoneOffset()

    if (!this.start_date_string || !this.end_date_string) {
      return []
    }

    const start = addMinutes(
      new Date(this.start_date_string),
      timeZoneOffsetMinutes
    )

    const end = addMinutes(
      new Date(this.end_date_string),
      timeZoneOffsetMinutes
    )

    const interval = eachDayOfInterval({ start, end })
    return interval.map(date => format(date, 'eee, MMM d, yyy'))
  }

  constructor(data: Record<string, unknown>) {
    this.user_defined_destinations = (data.user_defined_destinations as ItineraryDestination[]) ?? []
    this.is_using_day_count = (data.is_using_day_count as boolean | undefined) ?? true
    this.day_count = (data.day_count as number | undefined) ?? 1
    this.traveler_count = (data.traveler_count as number | undefined) ?? 1
    this.start_date_string = data.start_date_string as string | undefined
    this.end_date_string = data.end_date_string as string | undefined
    this.day_notes = data.day_notes
  }

  formatDateAsString(short: boolean = false): string | undefined {
    if (this.start_date_string && this.end_date_string) {
      const timeZoneOffsetMinutes = new Date('2024-01-01').getTimezoneOffset()
      const startDate = addMinutes(
        new Date(this.start_date_string),
        timeZoneOffsetMinutes
      )
      const endDate = addMinutes(
        new Date(this.end_date_string),
        timeZoneOffsetMinutes
      )

      const dateFormat = short ? 'M/d/yy' : 'eee, MMM d, yyy'
      return `${format(startDate, dateFormat)} - ${format(endDate, dateFormat)}`
    } else {
      return undefined
    }
  }
}

/**
 * Base post interface with common fields
 * 
 * This interface defines the common structure shared by all post types.
 * Specific post types (ListPost, ItineraryPost) extend this interface.
 */
export interface BasePost {
  _id: string
  status: string
  post_type: PostType
  cities: City[]
  name?: string
  name_suffix?: string
  body: string
  image_urls: string[]
  tag_list?: string[]
  category_ids: string[]
  user?: User
  circle_user?: CircleUser
  comment_count: number
  reply_post_count: number
  total_replies: number
  recommendation_count?: number
  explicit_recommendation_count?: number // NEW: Explicit recommendations only
  auto_recommendation_count?: number // NEW: Auto recommendations only
  recommendations?: unknown[]
  categories: Category[]
  city_identifying_names: string[]
  reply_to_post_id?: string
  is_hidden: boolean
  is_private: boolean
  is_draft: boolean
  is_pinned?: boolean
  is_saved?: boolean
  published_at?: Date
  created_at: Date
  updated_at?: Date
  displayUser: DisplayUser
  collaborators_email: string[]
  budget: string
  vibe_description: string
  allLargeImageUrls: string[]
  likes: Like[]
  saves: Save[]
  // Remix attribution fields
  remix_parent_post_id?: string
  remix_parent_creator_id?: string
  remix_parent_creator_name?: string
  remix_source_post_id?: string
  remix_source_creator_id?: string
  remix_source_creator_name?: string
  remix_has_attribution?: boolean
  remix_created_at?: Date
  /** User-chosen destination (city, locality, or country). When present, takes precedence over city_identifying_names for display. */
  primary_destination?: PrimaryDestination | null
  view_count?: number
  lifetime_unique_sharer_count?: number
  /** Stable public handle used in /itinerary/<slug>-<short_id> and /list/<slug>-<short_id> URLs. Populated for Itinerary and List posts. */
  short_id?: string | null
}

/**
 * Discriminated union type for Post
 * 
 * This type allows TypeScript to narrow the type based on the post_type field.
 * Use type guards (isListPost, isItineraryPost) to narrow the type safely.
 * 
 * @example
 * ```typescript
 * import { debug } from '@/utils/logger';
 * function handlePost(post: Post) {
 *   if (isListPost(post)) {
 *     // TypeScript knows post is ListPost here
 *     debug('[handlePost] List post', { locationCount: post.postLocations.length });
 *   } else if (isItineraryPost(post)) {
 *     // TypeScript knows post is ItineraryPost here
 *     debug('[handlePost] Itinerary post', { dayCount: post.itinerary_data?.day_count });
 *   }
 * }
 * ```
 */
export type PostUnion = ListPost | ItineraryPost | Post

/**
 * Type alias for backward compatibility
 * 
 * Post can be either the Post class instance or a discriminated union type.
 * Use PostUnion for discriminated union, Post class for class instances.
 */
export type PostTypeUnion = PostUnion

export default class Post {
  _id!: string
  status!: string
  post_type!: PostType
  cities!: City[]
  name?: string
  name_suffix?: string
  body!: string
  image_urls!: string[]
  tag_list?: string[]
  category_ids!: string[]
  user?: User
  circle_user?: CircleUser
  comment_count!: number
  reply_post_count!: number
  total_replies!: number
  recommendation_count?: number
  explicit_recommendation_count?: number
  auto_recommendation_count?: number
  recommendations?: unknown[]
  postLocations!: PostLocation[]
  post_locations?: PostLocation[]
  categories!: Category[]
  city_identifying_names: string[] = []
  reply_to_post_id?: string
  itinerary_data?: ItineraryData
  // Store raw itinerary_data for access to fields like day_notes that aren't in ItineraryData class
  raw_itinerary_data?: Record<string, unknown>
  is_hidden!: boolean
  is_private!: boolean
  is_draft!: boolean
  is_pinned?: boolean
  is_saved?: boolean
  published_at?: Date
  created_at!: Date
  updated_at?: Date
  displayUser!: DisplayUser
  collaborators_email: string[] = []
  budget: string = ''
  travel_with_types: string[] = []
  vibe_description: string = ''
  allLargeImageUrls: string[] = []
  place_location_type?: string // Only for use with Place post types, which have a single location.
  reply_to_post?: Post // This will have incomplete data since we're not doing a full query on the server.
  likes: Like[] = []
  saves: Save[] = []
  // Remix attribution fields
  remix_parent_post_id?: string
  remix_parent_creator_id?: string
  remix_parent_creator_name?: string
  remix_source_post_id?: string
  remix_source_creator_id?: string
  remix_source_creator_name?: string
  remix_has_attribution?: boolean
  remix_created_at?: Date
  // Post-level notes fields
  itinerary_notes?: string // Post-level notes for Itinerary posts
  list_notes?: string // Post-level notes for List posts
  /** Stable public handle used in /itinerary/<slug>-<short_id> and /list/<slug>-<short_id> URLs. Populated for Itinerary and List posts. */
  short_id?: string | null
  /** Provenance (Inspo, itinerary-import, etc.). Echo on full-replace updates or backend clears it. */
  source_metadata?: Record<string, unknown> | null
  /** User-chosen destination (city, locality, or country). When present, takes precedence over city_identifying_names for display. */
  primary_destination?: PrimaryDestination | null
  view_count?: number
  lifetime_unique_sharer_count?: number

  get displayDate() {
    if (!this.published_at) return this.created_at
    return this.published_at
  }

  get thumbnailPhotoURI() {
    return this.allLargeImageUrls[0] ?? ''
  }

  get locations(): Location[] {
    return this.postLocations.map(postLocation => postLocation.location)
  }

  get location_ids(): string[] {
    return this.postLocations.map(postLocation => postLocation.location._id)
  }

  get dataString(): string | undefined {
    if (this.post_type === PostType.List) {
      return `${this.postLocations.length} ${
        this.postLocations.length === 1 ? 'Place' : 'Places'
      }`
    }
    if (this.post_type === PostType.Itinerary) {
      return this.itinerary_data?.dataString ?? 'Itinerary'
    }
  }

  get itineraryDatesStringShort(): string | undefined {
    if (this.post_type === PostType.Itinerary) {
      return this.itinerary_data?.dataStringShort ?? 'Itinerary'
    }
  }

  get postLocationsSortedByItineraryDayIndex(): PostLocation[] {
    return [...this.postLocations].sort(
      (a, b) => a.itinerary_day_index - b.itinerary_day_index
    )
  }

  get postLocationsByItineraryDayIndex(): { [key: number]: PostLocation[] } {
    const postLocationsByDay: { [key: number]: PostLocation[] } = {}
    this.postLocations.forEach(postLocation => {
      const dayIndex = postLocation.itinerary_day_index ?? -1
      if (!postLocationsByDay[dayIndex]) {
        postLocationsByDay[dayIndex] = []
      }
      postLocationsByDay[dayIndex].push(postLocation)
    })
    return postLocationsByDay
  }

  get daysWithoutPostLocations(): number {
    if (!this.itinerary_data) return 0
    return this.itinerary_data?.day_count - this.daysWithPostLocations
  }

  get daysWithPostLocations(): number {
    if (!this.itinerary_data) return 0
    const validDayIndices = Object.keys(this.postLocationsByItineraryDayIndex)
      .map(Number)
      .filter(index => index >= 0)
    return validDayIndices.length
  }

  get hasPlannedAllDays(): boolean {
    if (!this.itinerary_data) return false
    return this.daysWithPostLocations >= this.itinerary_data?.day_count
  }

  get shouldHavePostLocations(): boolean {
    return (
      this.post_type === PostType.Itinerary || this.post_type === PostType.List
    )
  }

  get hasStayLocations(): boolean {
    return this.postLocations.some(postLocation => {
      return [
        'apartment_building',
        'apartment_complex',
        'bed_and_breakfast',
        'condominium_complex',
        'extended_stay_hotel',
        'farmstay',
        'guest_house',
        'hostel',
        'hotel',
        'housing_complex',
        'motel',
        'private_guest_room',
        'resort_hotel'
      ].some(type => {
        return type === postLocation.location.category_name
      })
    })
  }

  get stayLocations(): Location[] {
    return this.postLocations
      .filter(postLocation => {
        return [
          'apartment_building',
          'apartment_complex',
          'bed_and_breakfast',
          'condominium_complex',
          'extended_stay_hotel',
          'farmstay',
          'guest_house',
          'hostel',
          'hotel',
          'housing_complex',
          'motel',
          'private_guest_room',
          'resort_hotel'
        ].some(type => type === postLocation.location.category_name)
      })
      .map(postLocation => postLocation.location)
  }

  get usesCreateQuestionPostScreen(): boolean {
    return (
      this.post_type === PostType.Community ||
      this.post_type === PostType.Question
    )
  }

  get usesCreatePlaceRecommendationPostScreen(): boolean {
    return this.post_type === PostType.Place
  }

  get canBeRepliedTo(): boolean {
    return (
      this.post_type === PostType.Place ||
      this.post_type === PostType.Community ||
      this.post_type === PostType.Question ||
      this.post_type === PostType.Comment
    )
  }

  get useSmallerPostImages(): boolean {
    return (
      this.post_type === PostType.Place ||
      this.post_type === PostType.Community ||
      this.post_type === PostType.Question ||
      this.post_type === PostType.Comment ||
      this.post_type === PostType.Reply
    )
  }

  get shouldShowPostTypePill(): boolean {
    return (
      this.post_type !== PostType.Place &&
      this.post_type !== PostType.List &&
      this.post_type !== PostType.Reply &&
      this.post_type !== PostType.Community &&
      this.post_type !== PostType.Question
    )
  }

  get highlightsPostLocationImages(): boolean {
    return (
      this.post_type === PostType.List || this.post_type === PostType.Itinerary
    )
  }

  get shouldShowActionButtons(): boolean {
    return this.post_type !== PostType.Reply
  }

  postLocationsForDayIndex(dayIndex: number): PostLocation[] {
    return this.postLocationsByItineraryDayIndex[dayIndex] ?? []
  }

  get nameOrBody(): string {
    return this.name ?? this.body
  }

  isLikedByUser(userId?: string): boolean {
    if (!userId) return false
    return this.likes.some(like => like.user_id === userId)
  }

  isSavedByUser(userId?: string): boolean {
    if (!userId) return false
    return this.saves.some(save => save.user_id === userId)
  }

  // Legacy getters for backwards compatibility (always return false without user context)
  get likedByAuthenticatedUser(): boolean {
    return false
  }

  get savedByAuthenticatedUser(): boolean {
    return false
  }

  constructor(data: Record<string, unknown>) {
    try {
      // API may return snake_case or camelCase
      // Declared string[], but payloads have sent strings and other non-array
      // values here; coerce those so consumers can rely on array methods.
      // Absent stays undefined — callers use `tag_list ?? fallback`.
      const rawTagList = data.tag_list ?? data.tagList
      this.tag_list =
        rawTagList == null
          ? undefined
          : Array.isArray(rawTagList)
            ? (rawTagList as string[])
            : coerceStringIdArray(rawTagList)
      this._id = data._id as string ?? ''
      this.status = data.status as string ?? ''
      this.post_type = data.post_type as PostType ?? PostType.List
      this.cities = data.cities
        ? (data.cities as Record<string, unknown>[]).map((city) => new City(city))
        : []
      this.collaborators_email = (data.collaborators_email as string[] | undefined) ?? []
      this.budget = (data.budget as string | undefined) ?? ''
      this.travel_with_types =
        (data.travel_with_types as string[] | undefined) ??
        (data.travelWithTypes as string[] | undefined) ??
        []
      this.vibe_description = (data.vibe_description as string | undefined) ?? ''
      this.name = data.name as string | undefined
      this.short_id = (data.short_id as string | null | undefined) ?? null
      this.body = (data.body as string | undefined) ?? ''
      this.image_urls = (data.image_urls as string[] | undefined) ?? []
      this.category_ids = (data.category_ids as string[] | undefined) ?? []
      this.user = data.user ? new User(data.user as Record<string, unknown>) : undefined
      if (data.circle_user) {
        this.circle_user = new CircleUser(data.circle_user as Record<string, unknown>)
      }
      this.comment_count = (data.comment_count as number | undefined) ?? 0
      this.reply_post_count = (data.reply_post_count as number | undefined) ?? 0
      this.total_replies = this.comment_count + this.reply_post_count
      this.recommendation_count = (data.recommendation_count as number | undefined) ?? 0
      this.explicit_recommendation_count = (data.explicit_recommendation_count as number | undefined) ?? 0
      this.auto_recommendation_count = (data.auto_recommendation_count as number | undefined) ?? 0
      this.recommendations = (data.recommendations as unknown[] | undefined) ?? []

      debug('[Post] Recommendation counts from API', {
        postId: this._id,
        postName: this.name,
        postType: this.post_type,
        recommendation_count: this.recommendation_count,
        explicit_recommendation_count: this.explicit_recommendation_count,
        auto_recommendation_count: this.auto_recommendation_count,
        raw_recommendation_count: data.recommendation_count,
        raw_explicit_recommendation_count: data.explicit_recommendation_count,
        raw_auto_recommendation_count: data.auto_recommendation_count,
        hasExplicitRecommendations: (this.explicit_recommendation_count ?? 0) > 0,
      })

      // API may return snake_case or camelCase (list save response often uses postLocations).
      // Itinerary create/update may return post_location rows under `locations`.
      const locationsAsRows = isPostLocationRowArray(data.locations) ? data.locations : undefined;
      const postLocationsRaw =
        (data.post_locations as Record<string, unknown>[] | undefined) ??
        (data.postLocations as Record<string, unknown>[] | undefined) ??
        locationsAsRows
      this.postLocations = postLocationsRaw?.length
        ? postLocationsRaw.map((postLocation) => new PostLocation(postLocation))
        : []
      this.post_locations = this.postLocations

      if (
        this.post_type !== PostType.Itinerary &&
        this.postLocations.length > 1
      ) {
        this.post_type = PostType.List
      }
      this.categories = data.categories
        ? (data.categories as Record<string, unknown>[])
            .filter((category) => category) // In case we have a non-existent category.
            .map((category) => new Category(category))
        : []
      this.reply_to_post_id = data.reply_to_post_id as string | undefined
      this.reply_to_post = data.reply_to_post ? new Post(data.reply_to_post as Record<string, unknown>) : undefined
      this.likes = data.likes
        ? (data.likes as Record<string, unknown>[]).map((like) => new Like(like))
        : []
      this.saves = data.saves
        ? (data.saves as Record<string, unknown>[]).map((save) => new Save(save))
        : []
      this.city_identifying_names = Array.isArray(
        data.city_identifying_names
      )
        ? data.city_identifying_names as string[]
        : data.city_identifying_name
        ? [data.city_identifying_name as string]
        : []
      if (
        this.post_type === PostType.Itinerary ||
        this.post_type === PostType.List
      ) {
        // Store raw itinerary_data before converting to ItineraryData instance
        // This preserves fields like day_notes that aren't in the ItineraryData class
        this.raw_itinerary_data = data.itinerary_data as Record<string, unknown> | undefined
        this.itinerary_data = new ItineraryData((data.itinerary_data as Record<string, unknown> | undefined) ?? {})
      }

      this.is_hidden = (data.is_hidden as boolean | undefined) ?? false
      this.is_private = (data.is_private as boolean | undefined) ?? false
      this.is_draft = (data.is_draft as boolean | undefined) ?? false
      this.is_pinned = (data?.is_pinned as boolean | undefined) ?? false
      this.is_saved = (data?.is_saved as boolean | undefined) ?? false

      if (this.is_hidden) this.name_suffix = ' (Hidden)'
      if (this.is_private) this.name_suffix = ' (Private)'
      if (this.is_draft) this.name_suffix = ' (Draft)'

      if (this.is_private && this.is_draft)
        this.name_suffix = ' (Private Draft)'

      this.published_at = data.published_at ? new Date(data.published_at as string) : undefined
      this.created_at = new Date(data.created_at as string)
      this.updated_at = data.updated_at ? new Date(data.updated_at as string) : undefined

      // Remix attribution fields
      this.remix_parent_post_id = data.remix_parent_post_id as string | undefined
      this.remix_parent_creator_id = data.remix_parent_creator_id as string | undefined
      this.remix_parent_creator_name = data.remix_parent_creator_name as string | undefined
      this.remix_source_post_id = data.remix_source_post_id as string | undefined
      this.remix_source_creator_id = data.remix_source_creator_id as string | undefined
      this.remix_source_creator_name = data.remix_source_creator_name as string | undefined
      this.remix_has_attribution = data.remix_has_attribution as boolean | undefined
      this.remix_created_at = data.remix_created_at ? new Date(data.remix_created_at as string) : undefined
      // Post-level notes fields
      this.itinerary_notes = data.itinerary_notes as string | undefined
      this.list_notes = data.list_notes as string | undefined
      this.source_metadata =
        data.source_metadata != null && typeof data.source_metadata === 'object'
          ? (data.source_metadata as Record<string, unknown>)
          : undefined
      this.primary_destination = (data.primary_destination as PrimaryDestination | null | undefined) ?? null
      this.view_count = (data.view_count as number | undefined) ?? 0
      const lifetimeUniqueSharerCountRaw =
        (data.lifetime_unique_sharer_count as number | undefined) ??
        (data.lifetimeUniqueSharerCount as number | undefined)
      this.lifetime_unique_sharer_count =
        typeof lifetimeUniqueSharerCountRaw === 'number' ? lifetimeUniqueSharerCountRaw : undefined
      if (this.user) {
        this.displayUser = this.user.displayUser
      } else if (this.circle_user) {
        this.displayUser = this.circle_user.displayUser
      } else {
        this.displayUser = new DisplayUser({})
      }
      // Migrate old S3 bucket URLs to new bucket
      // Also handle photo identifier objects (backend now sends identifiers, not URLs)
      if (this.image_urls?.length > 0) {
        this.image_urls = this.image_urls.map((url: string | Record<string, unknown>) => {
          if (typeof url !== 'string') {
            // Handle new photo identifier format (backend sends identifiers only)
            if (url?.name || url?.photo_reference) {
              const photoUrl = getPhotoUrl(url as PhotoIdentifier, 400)
              if (photoUrl) {
                return photoUrl
              }
              // If conversion failed, log warning but continue
              warn('[Post] Failed to convert photo identifier to URL:', {
                hasName: !!url.name,
                hasPhotoReference: !!url.photo_reference,
                photoKeys: Object.keys(url),
              })
              return '' // Return empty string instead of object
            }
            // Unknown format - log warning
            warn('[Post] Image URL is not a string and not a photo identifier:', {
              urlType: typeof url,
              urlKeys: url ? Object.keys(url) : [],
              url,
            })
            return '' // Return empty string instead of object
          }
          return url
            .replace(
              'globethriversmedia112710-prod.s3.eu-west-1.amazonaws.com',
              'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
            )
            .replace(
              'globethriversmedia112710-prod.s3-accelerate.amazonaws.com',
              'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
            )
        }).filter((url): url is string => typeof url === 'string' && url.length > 0) // Filter out empty strings
      }

      this.allLargeImageUrls = this.image_urls
      if (this.postLocations?.length > 0) {
        if (this.highlightsPostLocationImages) {
          this.postLocations.forEach(postLocation => {
            this.allLargeImageUrls = this.allLargeImageUrls.concat(
              postLocation.image_urls
            )
          })
          this.postLocations.forEach(postLocation => {
            if (postLocation?.location?.largePhotoURI)
              this.allLargeImageUrls.push(postLocation.location.largePhotoURI)
          })
          if (this.allLargeImageUrls?.length === 0) {
            this.cities.forEach(city => {
              if (city?.largePhotoURI)
                this.allLargeImageUrls.push(city.largePhotoURI)
            })
          }
          if (this.allLargeImageUrls?.length === 0) {
            if (this.user?.city?.largePhotoURI)
              this.allLargeImageUrls.push(this.user?.city?.largePhotoURI)
          }
          if (this.allLargeImageUrls?.length === 0) {
            if (this.user?.city?.largePhotoURI)
              this.allLargeImageUrls.push(this.user?.city?.largePhotoURI)
          }
        }

        if (
          this.post_type === PostType.Place &&
          this.postLocations?.[0].location?.googlePlacesPrimaryTypeDisplayName
        ) {
          this.place_location_type =
            this.postLocations[0].location.googlePlacesPrimaryTypeDisplayName
        }
      }

      // For List posts: if allLargeImageUrls is still empty (e.g. user deleted their uploaded
      // cover photo and the DB has no image_urls), fall back to the city Google photo so
      // cards on the homepage/profile always show the city hero rather than the default palm tree.
      if (
        this.allLargeImageUrls.length === 0 &&
        this.post_type === PostType.List &&
        this.cities?.length > 0
      ) {
        const cityPhoto = this.cities[0].largePhotoURI
        if (cityPhoto) {
          this.allLargeImageUrls = [cityPhoto]
        }
      }
    } catch (err) {
      error('Error constructing Post:', err)
    }
  }

  locationExists(location: Location): boolean {
    return this.postLocations.some(
      postLocation => postLocation.location._id === location._id
    )
  }

  allLargeImageUrlsForPostingPreview(imageAssetOrUris: ImageAssetOrUri[]) {
    const incomingImageUrls = imageAssetOrUris.map(
      imageAssetOrUri => imageAssetOrUri.uri
    )
    return [
      ...incomingImageUrls,
      ...this.allLargeImageUrls.slice(this.image_urls.length)
    ]
  }

  static citiesFromPosts(posts: Post[]): City[] {
    const cities: City[] = []
    posts.forEach(post => {
      post.cities.forEach(city => {
        if (!cities.some(c => c._id === city._id)) {
          cities.push(city)
        }
      })
    })
    return cities
  }

  static countriesFromPosts(posts: Post[]): string[] {
    const countries: string[] = []
    posts.forEach(post => {
      post.cities.forEach(city => {
        if (city.country && !countries.includes(city.country)) {
          countries.push(city.country)
        }
      })
    })
    return countries
  }
}
